import {
  DeleteOutlined as DeleteIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import {
  Box,
  Menu,
  MenuItem,
  SelectChangeEvent,
  TableHead,
  TableRow,
  TabProps,
  Typography,
} from '@mui/material';
import { isEmpty, toString } from 'lodash';
import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import EmptyIcon from 'src/assets/images/NoDaypartEnabled.svg';
import { Header } from 'src/components/header';
import { Progress } from 'src/components/progress';
import { Column } from 'src/components/table';
import { StyledLocationSortButton } from 'src/components/table/CustomTable.styled';
import { CustomTableWithPagination } from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import { RowsPerPageOptions } from 'src/components/table/CustomTableWithPagination/TableControlsHeader/TableControlsHeader.interface';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import {
  DEFAULT_POSITION_ALL,
  getNoDaypartMessage,
  layoutTypeToPositionsKey,
  LISTVIEW_DROPDOWN_OPTIONS,
} from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import { ActiveDayIndicators } from 'src/features/locations/calendarLocationPage/modules/ActiveDayIndicators';
import { LocationCalendarHeader } from 'src/features/locations/calendarLocationPage/modules/CalendarLocationHeader/LocationCalendarHeader';
import { AssemblyStatusChip } from 'src/features/locations/modules/AssemblyStatusChip';
import { VIEW_OPTIONS } from 'src/features/locations/scheduleLocationGroupPage/modules/LocationSchedulePageTable/LocationSchedulePageTable.interface';
import {
  SortArrowDownIconStyled,
  SortArrowUpIconStyled,
} from 'src/features/locations/scheduleLocationGroupPage/modules/LocationSchedulePageTable/LocationSchedulePageTable.styled';
import { useGetLocationActiveDays, useMenu } from 'src/hooks';
import { IMappedLocationGroupDaypart } from 'src/interfaces';
import { DataTestID, lang } from 'src/utils';

import { ActionBar } from '../../../scheduleLocationGroupPage/modules/actionBar/ActionBar';
import { OOLocationSchedulePageFormValues } from '../../ScheduleLocationPageOO.interface';
import { LocationScheduleAccordionRow } from './LocationScheduleAccordionRow';
import { useOOLocationScheduleTable } from './OOLocationSchedulePageTable.hook';
import { OOLocationSchedulePageTableProps } from './OOLocationSchedulePageTable.interface';
import {
  ArrowIcon,
  BlankStateImg,
  BlankStateWrapper,
  ButtonGroupWrapper,
  Container,
  CustomTableCell,
  CustomTableCellSchedule,
  CustomTableContainer,
  DropdownContainer,
  FlexRowCenter,
  HeaderContainer,
  NoWrapTypography,
  RoundedDropdown,
  SortIcons,
  StyledIconButton,
  StyledMenuItem,
  StyledPageMenuItem,
  StyledTab,
  TableHeadCell,
  Tabs,
  TopResultContainer,
} from './OOLocationSchedulePageTable.styled';

export const Tab = (props: TabProps) => {
  return (
    <CustomTooltip
      width={500}
      placement="bottom"
      title={props.label}
      overflowWidth={360}>
      <StyledTab {...props} />
    </CustomTooltip>
  );
};

export const LinkedTitleColumn = ({
  title,
  isdoublewidth,
  linkTo,
}: {
  title: string;
  isdoublewidth: string;
  linkTo: string;
}) => {
  return (
    <CustomTableCell $isdoublewidth={isdoublewidth} $isAssemblyName>
      <CustomTooltip
        width={500}
        placement="bottom"
        title={title}
        overflowLineNumber={1}>
        <Link to={linkTo} target="_blank">
          {title}
        </Link>
      </CustomTooltip>
    </CustomTableCell>
  );
};
export const CustomCellRenderer = {
  assemblyName: (row: any, column: Column) => (
    <LinkedTitleColumn
      key={column.id}
      title={row[column.id]}
      isdoublewidth={column.id}
      linkTo={PATHS.ASSEMBLY_DETAILS(row.assemblyData?.id)}
    />
  ),
  componentName: (row: any, column: Column) => {
    const componentId = row.component_id;

    return (
      <LinkedTitleColumn
        title={row[column.id]}
        isdoublewidth={column.id}
        linkTo={PATHS.COMPONENT_DETAILS(componentId)}
      />
    );
  },
  status: (row: any, column: Column) => (
    <AssemblyStatusChip status={row[column.id]} />
  ),
};

export const OOLocationSchedulePageTable = (
  props: OOLocationSchedulePageTableProps,
) => {
  const { locationCode = '' } = useParams();
  const locationPath = useLocation();
  const isListActive = !!locationPath?.pathname?.includes('/schedule');
  const navigate = useNavigate();

  const key = props.locationGroup?.layoutType?.name
    ? layoutTypeToPositionsKey[props.locationGroup?.layoutType?.name]
    : undefined;
  const totalPositions = key ? props.projectConfig?.[key] : undefined;
  const isDefaultAssembly =
    props.selectedOption === VIEW_OPTIONS.DEFAULT_ASSEMBLY;
  const selectedRowRef = useRef<{ id: string; name: string } | null>(null);
  const formMethods = useFormContext<OOLocationSchedulePageFormValues>();

  const { locationGroupId, sortTableData } = useOOLocationScheduleTable(
    props.tableData as IMappedLocationGroupDaypart[],
    props.sortDirection.isSortModified,
  );

  const { data: activeDays = [] } = useGetLocationActiveDays(
    locationCode,
    props.locationGroup?.id,
  );

  const handleDropdownChange = (
    event: React.ChangeEvent<{ value: unknown }>,
  ) => {
    props.onSelectedOptionChange(event.target.value as string);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const handleToggle = (idx: number) => {
    setExpandedRow((prev) => (prev === idx ? null : idx));
  };
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    scheduleId: string,
    name: string,
  ) => {
    setAnchorEl(event.currentTarget);
    selectedRowRef.current = { id: scheduleId, name };
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const headers = props.columns.map((column) => column.label);

  const [location] = formMethods.watch(['location']);
  const startIndex = (props.page - 1) * props.rowsPerPage + 1;
  const endIndex = Math.min(props.page * props.rowsPerPage, props.totalCount);
  const rowsPerPageMenu = useMenu();
  const rowsPerPageItems = [
    {
      label: String(RowsPerPageOptions.TWENTY),
      isSelected: props.rowsPerPage === RowsPerPageOptions.TWENTY,
      onClick: () => props.onRowsPerPageChange(RowsPerPageOptions.TWENTY),
    },
    {
      label: String(RowsPerPageOptions.FIFTY),
      isSelected: props.rowsPerPage === RowsPerPageOptions.FIFTY,
      onClick: () => props.onRowsPerPageChange(RowsPerPageOptions.FIFTY),
    },
    {
      label: String(RowsPerPageOptions.ONE_HUNDRED),
      isSelected: props.rowsPerPage === RowsPerPageOptions.ONE_HUNDRED,
      onClick: () => props.onRowsPerPageChange(RowsPerPageOptions.ONE_HUNDRED),
    },
    {
      label: RowsPerPageOptions.ALL as string,
      isSelected: props.rowsPerPage === props.totalCount,
      onClick: () => props.onRowsPerPageChange(props.totalCount),
    },
  ];
  const noDaypartMessage = getNoDaypartMessage(props.shouldShowListview);

  return (
    <Container>
      <Header
        title={toString(location?.hq?.name)}
        actionBar={
          <ActionBar
            sortTableData={sortTableData}
            headers={headers as string[]}
          />
        }
        breadcrumbItems={[
          {
            label: lang.locationGroup,
            link: `${PATHS.OO_LOCATION_PAGE}`,
          },
          {
            label: lang.schedules,
          },
        ]}
      />

      <CustomTableContainer>
        {!isEmpty(location?.locationGroups) && (
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            value={Number(locationGroupId)}
            onChange={props.onChangeLocationGroup}>
            {location?.locationGroups?.map((item) => (
              <Tab
                disableRipple
                key={item.id}
                value={item.id}
                label={item.name}
              />
            ))}
          </Tabs>
        )}
        {location && (
          <Box>
            <LocationCalendarHeader
              selectedLocationGroup={location?.locationGroups?.find(
                (group) => group.id === Number(locationGroupId),
              )}
              scheduleDaypartsAvailable={!isEmpty(sortTableData)}
            />
          </Box>
        )}
        <HeaderContainer>
          <ButtonGroupWrapper>
            <StyledIconButton
              onClick={() =>
                navigate({
                  pathname: PATHS.LOCATION_CALENDAR(locationCode),
                  search: `groupID=${locationGroupId}`,
                })
              }>
              <CalendarTodayIcon />
            </StyledIconButton>
            <StyledIconButton active={isListActive}>
              <FormatListBulletedIcon />
            </StyledIconButton>
          </ButtonGroupWrapper>

          {activeDays.some((day) => day.active) && (
            <ActiveDayIndicators activeDays={activeDays} />
          )}
        </HeaderContainer>
        <TopResultContainer>
          <Typography>
            {!props.isLoading &&
              `${lang.results}: ${
                props.tableData.length
                  ? `${startIndex}–${endIndex} / ${props.totalCount}`
                  : `${endIndex} / ${props.totalCount}`
              }`}
          </Typography>
          <DropdownContainer>
            <RoundedDropdown
              id="viewSelector"
              onChange={handleDropdownChange}
              value={props.selectedOption}
              options={LISTVIEW_DROPDOWN_OPTIONS}
              MenuProps={{
                PaperProps: {
                  sx: { mt: 1 },
                },
              }}>
              {LISTVIEW_DROPDOWN_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RoundedDropdown>
            {totalPositions && !isDefaultAssembly && (
              <RoundedDropdown
                id="positionDropdown"
                data-testid="position-dropdown"
                disabled={props.shouldShowListview?.daypartDisabled}
                value={props.selectedPositionId}
                onChange={(e: SelectChangeEvent<string>) =>
                  props.onPositionChange(e.target.value)
                }
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 1,
                    },
                  },
                }}>
                {[...Array(totalPositions)].map((_, index) => {
                  const value = index + 1;

                  return (
                    <MenuItem key={value} value={value}>
                      {`Position ${value}`}
                    </MenuItem>
                  );
                })}
                <MenuItem value={DEFAULT_POSITION_ALL}>
                  {lang.scheduleComponent.positionDropdownAll}
                </MenuItem>
              </RoundedDropdown>
            )}
            <RoundedDropdown
              id="daypartSelector"
              value={props.selectedDaypartId}
              disabled={props.shouldShowListview?.daypartDisabled}
              onChange={(e: SelectChangeEvent<string>) =>
                props.onDaypartChange(Number(e.target.value))
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    mt: 1,
                  },
                },
              }}>
              {!props.isLoadingDayparts &&
                [...props.daypartOptions]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((daypart) => (
                    <MenuItem key={daypart.id} value={daypart.id}>
                      {daypart.name}
                    </MenuItem>
                  ))}
            </RoundedDropdown>
            <FlexRowCenter>
              <NoWrapTypography>{lang.itemsPerPage}</NoWrapTypography>
              <StyledLocationSortButton
                variant="text"
                endIcon={<UnfoldMoreIcon />}
                disableRipple
                disabled={
                  props.shouldShowListview?.daypartDisabled ||
                  props.shouldShowListview?.noScheduleComponent ||
                  props.shouldShowListview?.noDefaultAssembly
                }
                onClick={(
                  event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                ) => rowsPerPageMenu.onOpenMenu(event.currentTarget)}>
                {props.rowsPerPage}
              </StyledLocationSortButton>
              <Menu
                anchorEl={rowsPerPageMenu.anchorEl}
                onClose={rowsPerPageMenu.onCloseMenu}
                open={Boolean(rowsPerPageMenu.anchorEl)}>
                {rowsPerPageItems.map((item) => (
                  <StyledPageMenuItem
                    role="menuitem"
                    key={item.label}
                    selected={item.isSelected}
                    onClick={() => {
                      item.onClick();
                      rowsPerPageMenu.onCloseMenu();
                    }}>
                    {item.label}
                  </StyledPageMenuItem>
                ))}
              </Menu>
            </FlexRowCenter>
          </DropdownContainer>
        </TopResultContainer>

        {props.isLoading ? (
          <Progress data-testid="progress" />
        ) : noDaypartMessage ? (
          <BlankStateWrapper>
            <BlankStateImg
              src={EmptyIcon}
              alt="No records/daypart available"
              data-testid="emptyIcon"
              draggable={false}
            />
            <Typography variant="body2">{noDaypartMessage}</Typography>
          </BlankStateWrapper>
        ) : (
          <CustomTableWithPagination
            columns={props.columns}
            rows={isDefaultAssembly ? sortTableData : props.tableData}
            loading={props.isLoading}
            page={props.page}
            totalPages={props.totalPage}
            onPageChange={props.onPageChange}
            tableHeadRenderer={(columns) => {
              const CellComponent = isDefaultAssembly
                ? CustomTableCell
                : CustomTableCellSchedule;

              return (
                <TableHead
                  data-testid={DataTestID.LocationSchedulePageTableHead}>
                  <TableRow>
                    {!isDefaultAssembly && (
                      <CustomTableCellSchedule
                        sx={{ width: '48px' }}
                        $isHeader
                      />
                    )}

                    {columns.map((column) => (
                      <CellComponent
                        key={column.id}
                        $isdoublewidth={column.id === 'name'}
                        $isHeader
                        sx={{
                          textAlign: (column.align || 'left') as any,
                          width: column.width,
                        }}>
                        <TableHeadCell
                          sx={{
                            display: 'flex',
                            justifyContent:
                              column.align === 'right'
                                ? 'flex-end'
                                : 'flex-start',
                          }}>
                          {column.label}
                          {column.sortIcon && (
                            <SortIcons>
                              <ArrowIcon
                                data-testid={`sort-asc-${column.id}`}
                                onClick={() =>
                                  props.handleSortingClick(
                                    column.id,
                                    'ascending',
                                  )
                                }>
                                <SortArrowUpIconStyled
                                  data-testid="sort-icon-ascending"
                                  active={
                                    props.sortDirection.key ===
                                      column.sortKey &&
                                    props.sortDirection.direction ===
                                      'ascending' &&
                                    props.sortDirection.isSortModified
                                  }
                                />
                              </ArrowIcon>
                              <ArrowIcon
                                data-testid={`sort-desc-${column.id}`}
                                onClick={() =>
                                  props.handleSortingClick(
                                    column.id,
                                    'descending',
                                  )
                                }>
                                <SortArrowDownIconStyled
                                  data-testid="sort-icon-descending"
                                  active={
                                    props.sortDirection.key ===
                                      column.sortKey &&
                                    props.sortDirection.direction ===
                                      'descending' &&
                                    props.sortDirection.isSortModified
                                  }
                                />
                              </ArrowIcon>
                            </SortIcons>
                          )}
                        </TableHeadCell>
                      </CellComponent>
                    ))}
                    {!isDefaultAssembly && (
                      <CustomTableCellSchedule
                        sx={{ width: '48px' }}
                        $isHeader
                      />
                    )}
                  </TableRow>
                </TableHead>
              );
            }}
            customCellRenderer={CustomCellRenderer}
            totalCount={props.totalCount}
            renderAccordionRow={
              !isDefaultAssembly
                ? (row, idx) => (
                    <LocationScheduleAccordionRow
                      row={row}
                      idx={idx}
                      columns={props.columns}
                      expandedColumns={props.expandedColumns}
                      expandedRow={expandedRow}
                      onToggle={handleToggle}
                      onMenuOpen={handleMenuOpen}
                    />
                  )
                : undefined
            }
          />
        )}
      </CustomTableContainer>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        data-testid={DataTestID.LocationSchedulePageTableDeleteMenu}
        onClose={handleMenuClose}>
        <StyledMenuItem
          onClick={() => {
            if (selectedRowRef.current?.id) {
              props.onScheduleOverrideDelete(Number(selectedRowRef.current.id));
            }
            handleMenuClose();
          }}>
          <DeleteIcon fontSize="small" />
          {lang.deleteOverride}
        </StyledMenuItem>
      </Menu>
    </Container>
  );
};
